import { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Download, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui';
import { useNotification } from '@/hooks/useNotification';
import { onboardingService } from '../services/onboardingService';
import { workflowService } from '../services/workflowService';
import type { WorkflowSummary } from '../types/workflow';
import { resolveBackToListUrl } from '../utils/backToList';
import { CandidateDetailsPanel } from '../components/CandidateDetailsPanel';
import { CandidateSummary } from '../components/CandidateSummary';
import { CandidateTimeline } from '../components/CandidateTimeline';
import { CreateUserBanner } from '../components/CreateUserBanner';
import { MagicLinkPanel } from '../components/MagicLinkPanel';
import { NextActionsPanel } from '../components/NextActionsPanel';
import { WorkflowEventModal } from '../components/WorkflowEventModal';
import type { WorkflowNextEvent } from '../types/workflow';
import { downloadCandidateCsv } from '../utils/exportCandidate';

const ELIGIBLE_STATES = new Set(['onboarding_complete', 'bgv_verification_phase', 'completed']);

export const CandidateDetailPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const { id } = useParams<{ id: string }>();
  const numericId = id ? Number(id) : NaN;
  const [activeEvent, setActiveEvent] = useState<WorkflowNextEvent | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);

  /**
   * Return URL for the "Back to Candidates" affordance. The list
   * page passes its current URL (with active filters, workflow
   * tab, pagination) through `location.state.from` when it links
   * to a case — reading it back here restores the reviewer's
   * filter context exactly as they left it. Falls back to the
   * bare list URL for direct-URL loads, bookmarks, and new-tab
   * opens where `state.from` was never populated. See
   * `resolveBackToListUrl` for the shape-safety guards.
   */
  const backToList = useMemo(() => resolveBackToListUrl(location.state), [location.state]);
  const goBackToList = () => navigate(backToList);

  // Workflow + case state change as the candidate moves through events.
  // Disable cache freshness (staleTime: 0) and force a fetch on every mount
  // so navigating back to a detail page always pulls the latest state from
  // the server — the global QueryClient default of 5 minutes is too long
  // for a state-driven workflow UI.
  const candidateQuery = useQuery({
    queryKey: ['onboarding-case', numericId],
    queryFn: () => onboardingService.getCaseById(numericId),
    enabled: Number.isFinite(numericId) && numericId > 0,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  const workflowQuery = useQuery({
    queryKey: ['workflow-state', numericId],
    queryFn: () => workflowService.getState(numericId),
    enabled: Number.isFinite(numericId) && numericId > 0,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  // Workflows registry — cached across pages, cheap to co-load here.
  // Needed to resolve `candidate_case.workflow_id` → abbreviation so
  // the Create-user banner can gate on ONB (the endpoint it calls is
  // ONB-only under the new backend routing).
  const workflowsQuery = useQuery({
    queryKey: ['onboarding-workflows'],
    queryFn: () => onboardingService.getWorkflows(),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
  const caseWorkflowAbv = useMemo<string | undefined>(() => {
    const candidate = candidateQuery.data;
    if (!candidate) return undefined;
    // Try the direct `workflow_abv` field first (future-friendly if
    // the backend starts shipping it on case detail). Fall back to
    // resolving `workflow_id` against the registry so today's
    // payload also works.
    const direct = (candidate as { workflow_abv?: unknown }).workflow_abv;
    if (typeof direct === 'string' && direct !== '') return direct.toUpperCase();
    const workflows: WorkflowSummary[] = workflowsQuery.data ?? [];
    const workflowId = candidate.workflow_id;
    if (typeof workflowId !== 'number') return undefined;
    const match = workflows.find((w) => w.id === workflowId);
    return match?.abbreviation ? match.abbreviation.toUpperCase() : undefined;
  }, [candidateQuery.data, workflowsQuery.data]);

  const handleEventSubmitted = async () => {
    setActiveEvent(null);
    // The transition probably moved the case to a new state. A small
    // delay gives the backend a moment to propagate the write before we
    // read — without it, the immediate refetch sometimes returns the old
    // state. Await the refetches so the UI updates before the next user
    // action, and clear the list cache so the cases table also refreshes.
    await new Promise((resolve) => setTimeout(resolve, 300));
    await Promise.all([workflowQuery.refetch(), candidateQuery.refetch()]);
    queryClient.invalidateQueries({ queryKey: ['onboarding-cases'] });
  };

  if (!Number.isFinite(numericId) || numericId <= 0) {
    return (
      <div className="card">
        <div className="card-body text-center py-12 space-y-4">
          <p className="text-rgf-gray-700">Invalid candidate id.</p>
          <Link to={backToList}>
            <Button>Back to Candidates</Button>
          </Link>
        </div>
      </div>
    );
  }

  const handleExport = () => {
    const candidate = candidateQuery.data;
    if (!candidate) return;
    try {
      // Address is parsed off the case's `address` string directly in
      // `downloadCandidateCsv`, so no per-call lookup is needed any more.
      downloadCandidateCsv(candidate);
      showSuccess('Export Started', 'Candidate data CSV download has been triggered.');
    } catch (err: any) {
      showError('Export Failed', err?.message || 'Could not export candidate data.');
    }
  };

  const handleSyncToSharepoint = async () => {
    if (!Number.isFinite(numericId) || numericId <= 0 || isSyncing) return;
    setIsSyncing(true);
    try {
      const result = await onboardingService.syncToSharepoint([numericId]);
      showSuccess(
        'Synced to SharePoint',
        result?.message || 'This candidate was synced to SharePoint.',
      );
    } catch (err: any) {
      showError('Sync Failed', err?.message || 'Could not sync this candidate to SharePoint.');
    } finally {
      setIsSyncing(false);
    }
  };

  const candidateState = workflowQuery.data?.current_state ?? candidateQuery.data?.state;
  const canSyncToSharepoint = ELIGIBLE_STATES.has(candidateState ?? '');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-2">
        <Button
          variant="ghost"
          size="sm"
          icon={<ArrowLeft className="h-4 w-4" />}
          onClick={goBackToList}
        >
          Back to Candidates
        </Button>
        <div className="flex items-center gap-2">
          {canSyncToSharepoint && (
            <Button
              variant="secondary"
              size="sm"
              icon={<RefreshCw className="h-4 w-4" />}
              onClick={handleSyncToSharepoint}
              disabled={!candidateQuery.data}
              loading={isSyncing}
              title="Sync this candidate to SharePoint"
            >
              Sync to SharePoint
            </Button>
          )}
          <Button
            variant="secondary"
            size="sm"
            icon={<Download className="h-4 w-4" />}
            onClick={handleExport}
            disabled={!candidateQuery.data}
            title="Download this candidate's data as CSV (Field, Value)"
          >
            Export Data
          </Button>
          
        </div>
      </div>

      {candidateQuery.isLoading ? (
        <div className="card">
          <div className="card-body text-center text-rgf-gray-500 py-12">Loading candidate…</div>
        </div>
      ) : candidateQuery.error ? (
        <div className="card">
          <div className="card-body text-center py-12 space-y-4">
            <p className="text-rgf-gray-700">
              {(candidateQuery.error as Error)?.message || 'Failed to load this candidate.'}
            </p>
            <Button onClick={() => candidateQuery.refetch()}>Try Again</Button>
          </div>
        </div>
      ) : candidateQuery.data ? (
        <div className="space-y-6">
          <CreateUserBanner
            candidate={candidateQuery.data}
            liveState={workflowQuery.data?.current_state}
            workflowAbv={caseWorkflowAbv}
          />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <CandidateSummary
                candidate={candidateQuery.data}
                liveState={workflowQuery.data?.current_state}
              />
              <CandidateTimeline
                candidate={candidateQuery.data}
                liveState={workflowQuery.data?.current_state}
              />
              <CandidateDetailsPanel candidate={candidateQuery.data} />
            </div>
            <div className="space-y-6">
              <MagicLinkPanel
                candidateName={candidateQuery.data.contact?.name}
                candidatePhone={candidateQuery.data.contact?.phone_number}
                candidateEmail={candidateQuery.data.contact?.email}
                inviteToken={candidateQuery.data.invite_url_token}
              />
              <NextActionsPanel
                events={workflowQuery.data?.next_events ?? []}
                isLoading={workflowQuery.isLoading}
                isFetching={workflowQuery.isFetching}
                error={(workflowQuery.error as Error) || null}
                onSelect={setActiveEvent}
                onRefresh={() => workflowQuery.refetch()}
              />
            </div>
          </div>
        </div>
      ) : null}

      {activeEvent && (
        <WorkflowEventModal
          entityId={numericId}
          event={activeEvent}
          isOpen={!!activeEvent}
          onClose={() => setActiveEvent(null)}
          onSuccess={handleEventSubmitted}
        />
      )}
    </div>
  );
};
